import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, Send } from "lucide-react";
import { toast } from "sonner";
import { submitCountryQuote } from "@/lib/api";

const VERTICALS = ["", "restaurant", "hotel", "doctor", "school", "retail", "realtor", "other"];

export default function IndiaContact() {
  const [params] = useSearchParams();
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", business_name: "", city: "", state: "",
    vertical: params.get("vertical") || "", message: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const pkg = params.get("package") || "";
  const svc = params.get("service") || "";

  useEffect(() => { document.title = "WebsiteTech India · Get a Quote"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || (!form.email.trim() && !form.phone.trim())) {
      toast.error("Please add your name and an email or phone.");
      return;
    }
    setSaving(true);
    try {
      await submitCountryQuote("in", {
        ...form,
        package_interest: pkg || undefined,
        service_interest: svc || undefined,
        source: params.get("source") || "in-instance",
        page: window.location.pathname + window.location.search,
        campaign: params.get("utm_campaign") || undefined,
        referrer: document.referrer || undefined,
        utm: {
          source: params.get("utm_source") || undefined,
          medium: params.get("utm_medium") || undefined,
          campaign: params.get("utm_campaign") || undefined,
        },
      });
      setDone(true);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Couldn't submit. Please try again.");
    }
    setSaving(false);
  };

  const inputCls = "w-full bg-white border border-slate-200 rounded-lg px-3.5 py-3 text-slate-900 outline-none focus:border-brand text-sm";

  return (
    <div data-testid="india-contact">
      <section className="bg-slate-50 py-16 md:py-20 min-h-[70vh]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="overline text-brand">Get a Quote</span>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-900 mt-2">Tell us about your project</h1>
          <p className="text-slate-500 mt-3">We'll reply with a tailored ₹ proposal. GST-ready invoicing, UPI payments, WhatsApp support.</p>
          {(pkg || svc) && <p className="mt-3 inline-block bg-blue-50 text-brand text-sm font-semibold rounded-full px-3 py-1">Interested in: {pkg || svc}</p>}

          {done ? (
            <div className="bg-white border border-slate-200 rounded-2xl card-shadow p-10 mt-8 text-center" data-testid="india-quote-success">
              <CheckCircle2 className="text-emerald-500 mx-auto" size={44} />
              <h2 className="font-heading text-2xl font-bold text-slate-900 mt-4">Thank you! 🎉</h2>
              <p className="text-slate-500 mt-2">Your enquiry is in. The WebsiteTech India team will reach out shortly with next steps.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl card-shadow p-6 md:p-8 mt-8 space-y-4" data-testid="india-quote-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name *" className={inputCls} data-testid="quote-name" />
                <input value={form.business_name} onChange={(e) => set("business_name", e.target.value)} placeholder="Business name" className={inputCls} data-testid="quote-business" />
                <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" className={inputCls} data-testid="quote-email" />
                <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone / WhatsApp" className={inputCls} data-testid="quote-phone" />
                <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="City" className={inputCls} data-testid="quote-city" />
                <input value={form.state} onChange={(e) => set("state", e.target.value)} placeholder="State" className={inputCls} data-testid="quote-state" />
              </div>
              <select value={form.vertical} onChange={(e) => set("vertical", e.target.value)} className={inputCls} data-testid="quote-vertical">
                {VERTICALS.map((v) => <option key={v} value={v}>{v ? v.charAt(0).toUpperCase() + v.slice(1) : "Select your industry"}</option>)}
              </select>
              <textarea value={form.message} onChange={(e) => set("message", e.target.value)} rows={4} placeholder="What do you need? (website, redesign, AI, e-commerce, SEO…)" className={`${inputCls} resize-none`} data-testid="quote-message" />
              <button type="submit" disabled={saving} data-testid="quote-submit"
                className="w-full bg-brand text-white font-semibold rounded-xl px-5 py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:-translate-y-0.5 transition-transform">
                {saving ? <Loader2 className="animate-spin" size={18} /> : <><Send size={16} /> Send enquiry</>}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
